<template>
  <div>
    <div class="title">{{ $translate('Health') }}</div>
    <p class="mb-4">{{ $translate('Overview of errors and events across all your addons') }}</p>

    <!-- Filters -->
    <v-row class="mb-4" align="center">
      <v-col cols="12" sm="6" md="4">
        <v-select
          v-model="filters.status"
          :items="statusOptions"
          :label="$translate('Show')"
          multiple
          dense
          outlined
          hide-details
          @change="onFilterChange"
        >
          <template v-slot:selection="{ item }">
            <v-chip small class="mr-1" :color="item.value === 'with_errors' ? 'error' : 'success'" outlined>
              {{ item.text }}
            </v-chip>
          </template>
          <template v-slot:item="{ item, on, attrs }">
            <v-list-item v-on="on" v-bind="attrs">
              <v-chip small :color="item.value === 'with_errors' ? 'error' : 'success'" outlined>
                {{ item.text }}
              </v-chip>
            </v-list-item>
          </template>
        </v-select>
      </v-col>
      <v-col cols="12" sm="6" md="4" v-if="showSupplierFilter">
        <v-select
          v-model="filters.supplier"
          :items="supplierFilterOptions"
          :label="$translate('Supplier')"
          dense
          outlined
          hide-details
          @change="onSupplierFilterChange"
        ></v-select>
      </v-col>
      <v-col cols="auto">
        <span class="text-caption grey--text">{{ filteredAddons.length }} {{ $translate('of') }} {{ allAddons.length }}</span>
      </v-col>
    </v-row>

    <v-card v-if="loading" class="pa-4">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <span class="ml-4">{{ $translate('Loading...') }}</span>
    </v-card>

    <v-card v-else-if="filteredAddons.length === 0 && filters.status.includes('with_errors')" class="pa-6 text-center">
      <v-icon color="success" size="64" class="mb-4">mdi-check-circle</v-icon>
      <div class="text-h6">{{ $translate('All good!') }}</div>
      <p class="grey--text">{{ $translate('No errors to report') }}</p>
    </v-card>

    <v-card v-else-if="filteredAddons.length === 0" class="pa-4">
      <p>{{ $translate('No addons match filter') }}</p>
    </v-card>

    <div v-else>
      <v-row>
        <v-col cols="12" md="6" lg="4" v-for="addon in filteredAddons" :key="addon.addon_id">
          <v-card
            :class="{'error-card': addon.new_error_count > 0}"
            @click="goToAddon(addon.addon_id)"
            class="addon-card"
          >
            <v-card-title class="d-flex justify-space-between align-center">
              <div class="addon-name-container">
                <span class="addon-name">{{ addon.addon_name }}</span>
                <span v-if="(hasMultipleSuppliers || showAllSuppliers) && addon.supplier_name" class="text-caption grey--text d-block">{{ addon.supplier_name }}</span>
              </div>
              <v-chip
                v-if="addon.new_error_count > 0"
                color="error"
                small
              >
                {{ addon.new_error_count }} {{ $translate(addon.new_error_count === 1 ? 'new' : 'new_plural') }}
              </v-chip>
              <v-icon v-else color="success">mdi-check-circle</v-icon>
            </v-card-title>
            <v-card-actions>
              <v-btn text color="primary" @click.stop="goToAddon(addon.addon_id)">
                {{ $translate('View events') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
export default {
  name: 'health-component',
  data() {
    return {
      loading: true,
      allAddons: [],
      filters: {
        status: ['with_errors'],
        supplier: 'own'  // 'own', 'all', or a specific supplier_id
      }
    }
  },
  computed: {
    statusOptions() {
      return [
        { text: this.$translate('With errors'), value: 'with_errors' },
        { text: this.$translate('Without errors'), value: 'without_errors' }
      ];
    },
    isAdmin() {
      return this.$root.reviewer && this.$root.reviewer.list;
    },
    supplierOptions() {
      const supplierMap = {};
      this.allAddons.forEach(a => {
        if (a.supplier_id && a.supplier_name) {
          supplierMap[a.supplier_id] = a.supplier_name;
        }
      });
      return Object.entries(supplierMap)
        .map(([id, name]) => ({ text: name, value: id }))
        .sort((a, b) => a.text.localeCompare(b.text));
    },
    hasMultipleSuppliers() {
      return this.supplierOptions.length > 1;
    },
    showSupplierFilter() {
      return this.isAdmin || this.hasMultipleSuppliers;
    },
    supplierFilterOptions() {
      const options = [];
      if (this.isAdmin) {
        options.push({ text: this.$translate('All suppliers'), value: 'all' });
      }
      if (this.isAdmin || this.hasMultipleSuppliers) {
        options.push({ text: this.$translate('My suppliers'), value: 'own' });
      }
      return options.concat(this.supplierOptions);
    },
    showAllSuppliers() {
      return this.filters.supplier === 'all';
    },
    filteredAddons() {
      let result = this.allAddons;

      // Filter by supplier (if specific supplier selected)
      if (this.filters.supplier && this.filters.supplier !== 'own' && this.filters.supplier !== 'all') {
        result = result.filter(a => String(a.supplier_id) === String(this.filters.supplier));
      }

      // Filter by status
      if (this.filters.status.length === 1) {
        if (this.filters.status.includes('with_errors')) {
          result = result.filter(a => a.new_error_count > 0);
        } else if (this.filters.status.includes('without_errors')) {
          result = result.filter(a => a.new_error_count === 0);
        }
      }

      return result;
    }
  },
  mounted() {
    this.readUrlParams();
    this.loadSummary();
  },
  methods: {
    readUrlParams() {
      const query = this.$route.query;
      if (query.show) {
        this.filters.status = query.show.split(',');
      }
      if (query.supplier) {
        this.filters.supplier = query.supplier;
      }
    },
    onFilterChange() {
      const query = { ...this.$route.query };
      if (this.filters.status.length > 0 && this.filters.status.length < 2) {
        query.show = this.filters.status.join(',');
      } else {
        delete query.show;
      }
      if (this.filters.supplier && this.filters.supplier !== 'own') {
        query.supplier = this.filters.supplier;
      } else {
        delete query.supplier;
      }
      this.$router.replace({ query }).catch(() => {});
    },
    onSupplierFilterChange() {
      this.onFilterChange();
    },
    async loadSummary() {
      this.loading = true;
      try {
        let url = '/api/addon/supplier/events/summary';
        if (this.isAdmin) {
          url += '?show_all=true';
        }
        const response = await fetch(url);
        if (response.ok) {
          this.allAddons = await response.json();
          // Sort by error count descending, then by supplier, then by addon name
          this.allAddons.sort((a, b) => {
            if (b.new_error_count !== a.new_error_count) {
              return b.new_error_count - a.new_error_count;
            }
            if (a.supplier_id !== b.supplier_id) {
              return (a.supplier_name || '').localeCompare(b.supplier_name || '');
            }
            return a.addon_name.localeCompare(b.addon_name);
          });
        }
      } catch (e) {
        console.error('Failed to load events summary:', e);
      } finally {
        this.loading = false;
      }
    },
    goToAddon(addon_id) {
      this.$router.push({ name: 'show', params: { addon_id }, query: { tab: 'log', state: 'new', level: 'error' } });
    }
  }
}
</script>

<style scoped>
.addon-card {
  cursor: pointer;
  transition: box-shadow 0.2s;
}
.addon-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
.error-card {
  border-left: 4px solid #f44336;
}
::v-deep .v-card__title {
  min-width: 0;
}
.addon-name-container {
  overflow: hidden;
  min-width: 0;
  flex: 1;
  margin-right: 8px;
}
.addon-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}
</style>
